import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import Button from '@/components/ui/Button';
import { clientLogger } from '@/utils/clientLogger';

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Loading CSS chunk')
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    clientLogger.error('react_error_boundary', error, {
      component_stack: errorInfo.componentStack,
    });
  }

  handleReset = () => {
    if (this.state.error && isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const chunkError = this.state.error && isChunkLoadError(this.state.error);

      return (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="card w-full max-w-md p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {chunkError ? 'App Updated' : 'Something went wrong'}
            </h3>
            <p className="text-gray-600 mb-4">
              {chunkError
                ? 'A new version of the app is available. Please reload to get the latest version.'
                : 'An unexpected error occurred. Please try refreshing the page.'}
            </p>
            {!chunkError && this.state.error && (
              <p className="text-sm text-gray-400 mb-4 font-mono bg-gray-50 p-2 rounded">
                {this.state.error.message}
              </p>
            )}
            <Button onClick={this.handleReset}>
              <RefreshCw className="h-4 w-4" />
              {chunkError ? 'Reload Page' : 'Try Again'}
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
